import type { LucideIcon } from 'lucide-react'
import { useQuery } from '@apollo/client/react'
import { RoleEnum } from '@zeus/index'
import { AlertTriangle, CalendarClock, CalendarPlus, CircleCheckBig, UserRoundX } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { GQL_BACK_OFFICE_HOME } from '~/gql/home/home'
import { formatSessionDate } from '~/helpers/dates/session-dates'
import { useAuth } from '~/helpers/store/auth'
import { tourAnchor } from '~/helpers/tour/anchors'

/**
 * Nombre de faits listés par bloc. Une promo qui lance douze parcours le même
 * lundi ne doit pas repousser les chiffres sous la ligne de flottaison.
 */
const MAX_ROWS = 3

/** Un fait du jour : une phrase qui se lit à voix haute. */
type Fact = {
  key: string
  icon: LucideIcon
  tone: string
  title: string
  detail?: string
}

/**
 * Ligne d'information, volontairement NON cliquable.
 *
 * Elle l'a été, et c'était un lien mort : il pointait vers ce tableau de bord
 * avec `?sessionId=…` alors qu'on s'y trouve déjà. React Router ne remonte pas
 * l'écran pour un simple changement de query, et le filtre n'est lu qu'au
 * montage — l'URL changeait, l'écran ne bougeait pas. Un lien qui ne fait rien
 * est pire qu'une ligne inerte : on clique, on attend, on ne comprend pas.
 *
 * Le filtre de parcours est juste en dessous, sur la table, et fait ce travail.
 */
function FactRow({ fact }: { fact: Fact }) {
  return (
    <li className="flex items-center gap-3 px-3 py-2.5">
      <span className={`flex size-8 shrink-0 items-center justify-center rounded-full ${fact.tone}`}>
        <fact.icon className="size-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{fact.title}</span>
        {fact.detail && (
          <span className="block truncate text-xs text-muted-foreground">{fact.detail}</span>
        )}
      </span>
    </li>
  )
}

/**
 * Ce qui appelle une action aujourd'hui, en tête du tableau de bord.
 *
 * Hérite de l'accueil (#201) et de son parti pris principal, qui survit au
 * regroupement des deux écrans : **des phrases, pas des chiffres posés**. Un
 * nombre se contemple, une phrase se traite — les compteurs et les graphiques
 * en dessous répondent à « comment ça se passe », cette bande répond à
 * « qu'est-ce que j'ai à faire ».
 *
 * Elle disparaît entièrement quand il n'y a rien à signaler : une journée sans
 * urgence doit se voir comme telle, pas se meubler avec des blocs vides.
 */
function AlertStrip() {
  const { t, i18n } = useTranslation()
  const { payload } = useAuth()
  const { data, previousData } = useQuery(GQL_BACK_OFFICE_HOME, {
    fetchPolicy: 'cache-and-network',
  })

  const home = data?.backOfficeHome ?? previousData?.backOfficeHome
  const isAdmin = payload?.role === RoleEnum.Admin

  // Le fuseau vient du digest et non du branding : c'est celui-là même qui a
  // découpé les « aujourd'hui » et « dans 7 jours » côté serveur, donc l'écran
  // et ses chiffres ne peuvent pas se contredire.
  const formatDate = (value: unknown) => formatSessionDate(value, i18n.language, home?.timezone)

  const attention: Fact[] = home
    ? [
        ...home.overdue.map(session => ({
          key: `overdue-${session.id}`,
          icon: AlertTriangle,
          tone: 'bg-red-500/10 text-error-text',
          title: t('backHome.overdue.title', { name: session.name, count: session.pendingLearners }),
          detail: t('backHome.overdue.detail', { date: formatDate(session.endDate) }),
        })),
        ...home.endingSoon
          .filter(session => session.pendingLearners > 0)
          .slice(0, MAX_ROWS)
          .map(session => ({
            key: `ending-${session.id}`,
            icon: CalendarClock,
            tone: 'bg-amber-500/10 text-warning-text',
            title: t('backHome.endingSoon.title', { name: session.name, date: formatDate(session.endDate) }),
            detail: t('backHome.endingSoon.detail', { count: session.pendingLearners, total: session.enrolledLearners }),
          })),
        // Compté pour l'admin seul : le formateur n'ouvre pas la liste des
        // apprenants, ce chiffre ne lui ouvrirait aucune suite.
        ...(isAdmin && home.neverStartedLearners > 0
          ? [{
              key: 'never-started',
              icon: UserRoundX,
              tone: 'bg-muted text-muted-foreground',
              title: t('backHome.neverStarted.title', { count: home.neverStartedLearners }),
              detail: t('backHome.neverStarted.detail', { count: home.neverStartedLearners }),
            }]
          : []),
      ]
    : []

  const upcoming: Fact[] = (home?.startingSoon ?? []).slice(0, MAX_ROWS).map(session => ({
    key: `starting-${session.id}`,
    icon: CalendarPlus,
    tone: 'bg-sky-500/10 text-info-text',
    title: t('backHome.startingSoon.title', { name: session.name, date: formatDate(session.startDate) }),
    detail: t('backHome.startingSoon.detail', { count: session.enrolledLearners }),
  }))

  const today: Fact[] = (home?.completedToday ?? []).slice(0, MAX_ROWS).map(row => ({
    key: `done-${row.sessionId}-${row.moduleId}`,
    icon: CircleCheckBig,
    tone: 'bg-green-500/10 text-success-text',
    title: t('backHome.completedToday.title', { count: row.learners, module: row.moduleTitle }),
    detail: row.sessionName,
  }))

  const facts = [...attention, ...upcoming, ...today]
  if (facts.length === 0) return null

  /**
   * Deux écarts que les lignes de retard ne peuvent pas dire seules : plusieurs
   * parcours peuvent concerner les mêmes personnes, et la liste est tronquée
   * côté serveur alors que les compteurs portent sur la totalité. Les taire
   * laisserait le lecteur additionner les lignes et tomber sur un autre nombre.
   */
  const overdueNote = home && home.overdue.length > 1
    ? [
        t('backHome.overdue.learners', { count: home.overdueLearners }),
        ...(home.overdueSessions > home.overdue.length
          ? [t('backHome.overdue.more', { count: home.overdueSessions - home.overdue.length })]
          : []),
      ].join(' ')
    : undefined

  return (
    <section
      {...tourAnchor('dashboard.alerts')}
      aria-label={t('dashboard.alerts.title')}
      className="rounded-2xl border border-border bg-card p-2 shadow-sm"
    >
      <ul className="flex flex-col">
        {facts.map(fact => <FactRow key={fact.key} fact={fact} />)}
      </ul>
      {overdueNote && (
        <p className="px-3 pb-1 pt-2 text-xs text-muted-foreground">{overdueNote}</p>
      )}
    </section>
  )
}

export default AlertStrip
